import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  Chip,
  useTheme,
  IconButton
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Mic as MicIcon,
  SignalCellularConnectedNoInternet0Bar as DisconnectedIcon,
  SignalCellularConnectedNoInternet4Bar as ConnectedIcon
} from '@mui/icons-material';
import DeviceCard from './DeviceCard';
import ScoreGradientBar from './ScoreGradientBar';
import DisplayModeSwitch from './DisplayModeSwitch';
import { mockHistoryData, generateNewMockEvents } from '../libs/mockData';
import { USE_MOCK_DATA, streamEventToDevice } from './Devices';
import { fetchApiRequest, renderBoilerPlate, getApiHost } from './Common';
import { useSSE } from '../hooks/useSSE';

function DeviceDetail() {
  const theme = useTheme();
  const { deviceId } = useParams();

  const [currentEvent, setCurrentEvent] = useState(null);
  const [deviceHistory, setDeviceHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [displayMode, setDisplayMode] = useState('circular');
  const itemsPerPage = 20;

  // Live updates via SSE (with polling fallback)
  const { data: liveEvents, status: connectionStatus, error: sseError } = useSSE('/v1/events/stream', {
    fallbackToPolling: true,
    pollInterval: 10000,
  });



  // Function to format timestamp in Swedish format
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'No timestamp';
    try {
      const date = new Date(timestamp);
      const dateStr = date.toLocaleDateString('sv-SE');
      const timeStr = date.toLocaleTimeString('sv-SE', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });

      return `${dateStr} ${timeStr}`;
    } catch (error) {
      return 'Invalid timestamp';
    }
  };

  useEffect(() => {
    const fetchData = (allHistory) => {

      // Normalize timestamps to local time by subtracting 2 hours (UTC+2 to local)
      const normalizedHistory = allHistory.map(event => ({
        ...event,
        timestamp: new Date(new Date(event.timestamp).getTime() - (2 * 60 * 60 * 1000)).toISOString()
      }));

      // More robust filtering - check both device_id and name fields
      const filteredHistory = normalizedHistory.filter(record => {
        const deviceMatch = record.device_id === deviceId || record.name === deviceId;
        return deviceMatch;
      });

      // Sort by timestamp descending (most recent first)
      const sortedHistory = filteredHistory.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));



      if (sortedHistory.length > 0) {
        // Ensure the current event is for the selected device
        const deviceCurrentEvent = sortedHistory[0];
        const currentEventMatch = deviceCurrentEvent.device_id === deviceId || deviceCurrentEvent.name === deviceId;

        if (currentEventMatch) {
          setCurrentEvent(deviceCurrentEvent);
          setDeviceHistory(sortedHistory);
        } else {
          console.error('Data mismatch: current event device ID/name doesn\'t match selected device');
          setError("Data mismatch: current event device ID doesn't match selected device.");
        }
      } else {
        setError("No data found for this device.");
      }
      setLoading(false);
    };

    setLoading(true);
    setError(null);
    setDeviceHistory([]);
    setCurrentEvent(null);

    if (USE_MOCK_DATA) {
      // For mock data, normalize timestamps to current time
      const normalizedMockData = mockHistoryData.map(event => ({
        ...event,
        timestamp: new Date().toISOString()
      }));
      setTimeout(() => fetchData(normalizedMockData), 300);
    } else {
      getApiHost(apiHost => {
        const apiUrl = `${apiHost}/v1/devices/history?limit=1000&device_id=${encodeURIComponent(deviceId)}`;
        fetchApiRequest(apiUrl, (fetchPromise) => {
          fetchPromise
            .then(res => res.ok ? res.json() : Promise.reject('Network response was not ok'))
            .then(data => {
              fetchData(data);
            })
            .catch(err => {
              console.error('API error:', err);
              setError(err.toString());
              setLoading(false);
            });
        });
      });
    }
  }, [deviceId]);

  // Merge live updates for selected device
  useEffect(() => {
    if (!liveEvents) {
      return;
    }

    let updated = null;
    if (liveEvents.devices) {
      updated = liveEvents.devices.find((d) => (
        d.name === deviceId || d.device_id === deviceId
      ));
    } else if (liveEvents.device_id === deviceId) {
      updated = streamEventToDevice(liveEvents);
    }

    if (!updated) {
      return;
    }

    const normalized = {
      ...updated,
      device_id: updated.device_id || updated.name,
      name: updated.name || updated.device_id,
    };

    setCurrentEvent(prev => {
      const result = !prev ? normalized : (new Date(normalized.timestamp).getTime() >= new Date(prev.timestamp).getTime() ? normalized : prev);
      return result;
    });

    setDeviceHistory(prevHistory => {
      const isDuplicate = prevHistory.some(e => (
        (e.device_id === deviceId || e.name === deviceId) && e.timestamp === normalized.timestamp
      ));

      const next = isDuplicate ? prevHistory : [normalized, ...prevHistory];
      next.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

      return next;
    });
  }, [liveEvents, deviceId]);

  // Mock live updates for testing - generates new events every 15 seconds for selected device
  useEffect(() => {
    if (USE_MOCK_DATA && deviceId) {
      const mockInterval = setInterval(() => {
        // Generate a new event specifically for this device
        const newEvent = {
          ...generateNewMockEvents(),
          device_id: deviceId,
          name: deviceId,
          // Normalize timestamp to local time by subtracting 2 hours (UTC+2 to local)
          timestamp: new Date(new Date(generateNewMockEvents().timestamp).getTime() - (2 * 60 * 60 * 1000)).toISOString()
        };

        // Update current event if it's newer
        setCurrentEvent(prev => {
          const result = !prev ? newEvent : (new Date(newEvent.timestamp).getTime() >= new Date(prev.timestamp).getTime() ? newEvent : prev);
          return result;
        });

        // Add to history
        setDeviceHistory(prevHistory => {
          const isDuplicate = prevHistory.some(e => (
            (e.device_id === deviceId || e.name === deviceId) && e.timestamp === newEvent.timestamp
          ));

          const next = isDuplicate ? prevHistory : [newEvent, ...prevHistory];
          next.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

          return next;
        });
      }, 8000); // Generate new event every 8 seconds for faster testing

      return () => clearInterval(mockInterval);
    }
  }, [USE_MOCK_DATA, deviceId]);

  // Memory cleanup mechanism - reload entire history periodically to prevent memory buildup
  useEffect(() => {
    // Wait for initial data to load before starting cleanup
    if (deviceHistory.length === 0) return;

    const memoryCleanupInterval = setInterval(() => {

      // Store the current history temporarily to preserve any new events
      const currentHistory = [...deviceHistory];

      // Clear current history to free memory
      setDeviceHistory([]);
      setCurrentEvent(null);
      setCurrentPage(1);
      setLoading(true);

      if (USE_MOCK_DATA) {
        // For mock data, reload with fresh mock data and merge with any new events
        setTimeout(() => {
          const refreshedData = mockHistoryData.map(event => ({
            ...event,
            timestamp: new Date().toISOString() // Update timestamps to current time
          }));

          // Filter for current device
          const deviceRefreshedData = refreshedData.filter(record =>
            record.device_id === deviceId || record.name === deviceId
          );

          // Find any events in currentHistory that are newer than the mock data
          const mockDataTimestamp = new Date(deviceRefreshedData[0]?.timestamp || new Date()).getTime();
          const newerEvents = currentHistory.filter(event => {
            const eventTimestamp = new Date(event.timestamp).getTime();
            return eventTimestamp > mockDataTimestamp;
          });

          // Combine refreshed data with newer events
          const combinedData = [...newerEvents, ...deviceRefreshedData];

          const sortedRefreshedData = combinedData.sort((a, b) => {
            const dateA = new Date(a.timestamp);
            const dateB = new Date(b.timestamp);
            return dateB.getTime() - dateA.getTime();
          });



          if (sortedRefreshedData.length > 0) {
            setCurrentEvent(sortedRefreshedData[0]);
          }
          setDeviceHistory(sortedRefreshedData);
          setLoading(false);
        }, 500);
      } else {
        // For real API, fetch fresh data from scratch and merge with any new events
        getApiHost(apiHost => {
          const apiUrl = `${apiHost}/v1/devices/history?limit=1000&device_id=${encodeURIComponent(deviceId)}`;
          fetchApiRequest(apiUrl, (fetchPromise) => {
            fetchPromise
              .then(res => res.ok ? res.json() : Promise.reject('Network response was not ok'))
              .then(data => {
                // Normalize timestamps to local time by subtracting 2 hours
                const normalizedData = data.map(event => ({
                  ...event,
                  timestamp: new Date(new Date(event.timestamp).getTime() - (2 * 60 * 60 * 1000)).toISOString()
                }));

                const sortedData = normalizedData.sort((a, b) => {
                  const dateA = new Date(a.timestamp);
                  const dateB = new Date(b.timestamp);
                  return dateB.getTime() - dateA.getTime();
                });

                // Find any events in currentHistory that are newer than the API data
                const apiDataTimestamp = new Date(sortedData[0]?.timestamp || new Date()).getTime();
                const newerEvents = currentHistory.filter(event => {
                  const eventTimestamp = new Date(event.timestamp).getTime();
                  return eventTimestamp > apiDataTimestamp;
                });

                // Combine API data with newer events
                const combinedData = [...newerEvents, ...sortedData];

                const finalSortedData = combinedData.sort((a, b) => {
                  const dateA = new Date(a.timestamp);
                  const dateB = new Date(b.timestamp);
                  return dateB.getTime() - dateA.getTime();
                });



                if (finalSortedData.length > 0) {
                  setCurrentEvent(finalSortedData[0]);
                }
                setDeviceHistory(finalSortedData);
                setLoading(false);
              })
              .catch(err => {
                setError(err.toString());
                setLoading(false);
              });
          });
        });
      }
    }, 120000); // Cleanup every 2 minutes (120,000 ms)

    return () => clearInterval(memoryCleanupInterval);
  }, [deviceHistory.length, deviceId]);

  // Calculate pagination
  const filteredDeviceHistory = deviceHistory.filter(record =>
    record.device_id === deviceId || record.name === deviceId
  );
  const totalPages = Math.ceil(filteredDeviceHistory.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentHistory = filteredDeviceHistory.slice(startIndex, endIndex);

  if (loading) {
    return renderBoilerPlate(
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="calc(100vh - 64px)">
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (error) {
    return renderBoilerPlate(
      <Box p={3}>
        <Button
          component={Link}
          to="/devices"
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 2 }}
        >
          Back to Devices
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return renderBoilerPlate(
    <Box p={3}>
      <Button
        component={Link}
        to="/devices"
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 3 }}
      >
        Back to Devices
      </Button>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <MicIcon color="primary" sx={{ fontSize: 28, mr: 1 }} />
          <Typography
            variant="h4"
            component="h1"
            sx={{ color: theme.palette.text.primary }}
          >
            {deviceId}
          </Typography>
          {currentEvent && (
            <Box sx={{ ml: 2 }}>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary }}
              >
                Last update: {formatTimestamp(currentEvent.timestamp)}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: 'success.main', fontWeight: 'bold' }}
              >
                ● Auto-cleanup every 2min
              </Typography>
            </Box>
          )}
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          <Chip
            icon={connectionStatus?.connected ? <ConnectedIcon /> : <DisconnectedIcon />}
            label={connectionStatus?.text || 'Connecting...'}
            color={connectionStatus?.connected ? 'success' : 'error'}
            variant="outlined"
            sx={{ paddingLeft: '0.4rem' }}
            onClick={(e) => e.preventDefault()}
          />
          {currentEvent && (
            <Chip
              label={`${filteredDeviceHistory.length} events`}
              size="small"
              color="info"
              variant="outlined"
            />
          )}
        </Box>
      </Box>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography
          variant="h5"
          component="h2"
          sx={{ color: theme.palette.text.primary }}
        >
          Current Event
        </Typography>
        <DisplayModeSwitch
          value={displayMode}
          onChange={setDisplayMode}
        />
      </Box>

      <Box mb={4}>
        {currentEvent && (currentEvent.device_id === deviceId || currentEvent.name === deviceId) ? (
          <Box
            sx={{
              minHeight: '280px',
              display: 'flex',
              maxWidth: '320px',
              width: '100%'
            }}
          >
            <DeviceCard mic={currentEvent} isLink={false} displayMode={displayMode} />
          </Box>
        ) : (
          <Box textAlign="center" p={3}>
            <Typography variant="body2" color="text.secondary">
              No current event data available for this device
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ maxWidth: 'calc(3 * 320px + 2 * 8px)', width: '100%' }}>
        <Typography
          variant="h5"
          component="h2"
          gutterBottom
          sx={{ color: theme.palette.text.primary, mb: 2 }}
        >
          Device History ({filteredDeviceHistory.length} events)
        </Typography>
        {currentHistory.length > 0 ? (
          <>
            <TableContainer
              component={Paper}
              sx={{
                backgroundColor: theme.palette.background.default,
                border: `1px solid ${theme.palette.divider}`,
                overflow: 'auto'
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        backgroundColor: theme.palette.background.default,
                        color: theme.palette.text.primary,
                        fontWeight: 'bold',
                        padding: '12px 0',
                        fontSize: '1.2rem',
                        width: '50%',
                        textAlign: 'left'
                      }}
                    >
                      Timestamp
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: theme.palette.background.default,
                        color: theme.palette.text.primary,
                        fontWeight: 'bold',
                        padding: '12px 0',
                        fontSize: '1.2rem',
                        width: '50%',
                        textAlign: 'left'
                      }}
                    >
                      Events & Scores
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentHistory
                    .filter(record => record.device_id === deviceId || record.name === deviceId)
                    .map((record, index) => (
                    <TableRow
                      key={index}
                      hover
                      sx={{
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <TableCell sx={{
                        color: theme.palette.text.primary,
                        padding: '12px 0',
                        width: '50%',
                        textAlign: 'left'
                      }}>
                        {formatTimestamp(record.timestamp)}
                      </TableCell>
                      <TableCell sx={{
                        color: theme.palette.text.primary,
                        padding: '12px 0',
                        width: '50%',
                        textAlign: 'left'
                      }}>
                        {record.events && record.scores ? (
                          <Box>
                            {(() => {
                              if (displayMode === 'circular') {
                                // For circular mode: 3 per row max
                                const rows = [];
                                for (let i = 0; i < record.events.length; i += 3) {
                                  const rowEvents = record.events.slice(i, i + 3);
                                  const rowScores = record.scores.slice(i, i + 3);
                                  rows.push(
                                    <Box key={i} sx={{ display: 'flex', gap: 0.5, mb: i + 3 < record.events.length ? 0.2 : 0, justifyContent: 'flex-start', ml: 1 }}>
                                      {rowEvents.map((event, eventIndex) => (
                                        <Box
                                          key={eventIndex}
                                          sx={{
                                            minWidth: '120px',
                                            width: '120px',
                                            flex: '0 0 120px'
                                          }}
                                        >
                                          <ScoreGradientBar
                                            score={rowScores[eventIndex]}
                                            event={event}
                                            height={32}
                                            width="100%"
                                            displayMode={displayMode}
                                          />
                                        </Box>
                                      ))}
                                    </Box>
                                  );
                                }
                                return rows;
                              } else {
                                // For bar mode: 3 per row max (same as circular)
                                const rows = [];
                                for (let i = 0; i < record.events.length; i += 3) {
                                  const rowEvents = record.events.slice(i, i + 3);
                                  const rowScores = record.scores.slice(i, i + 3);
                                  rows.push(
                                    <Box key={i} sx={{ display: 'flex', gap: 0.5, mb: i + 3 < record.events.length ? 0.2 : 0, justifyContent: 'flex-start', ml: 1 }}>
                                      {rowEvents.map((event, eventIndex) => (
                                        <Box
                                          key={eventIndex + i}
                                          sx={{
                                            minWidth: '120px',
                                            width: '120px',
                                            flex: '0 0 120px'
                                          }}
                                        >
                                          <ScoreGradientBar
                                            score={rowScores[eventIndex]}
                                            event={event}
                                            height={32}
                                            width="100%"
                                            displayMode={displayMode}
                                          />
                                        </Box>
                                      ))}
                                    </Box>
                                  );
                                }
                                return rows;
                              }
                            })()}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No events data
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  mt: 2,
                  gap: 1
                }}
              >
                <IconButton
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  size="small"
                >
                  <ChevronLeftIcon />
                </IconButton>

                <Typography variant="body2" sx={{ mx: 2 }}>
                  Page {currentPage} of {totalPages}
                </Typography>

                <IconButton
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  size="small"
                >
                  <ChevronRightIcon />
                </IconButton>
              </Box>
            )}
          </>
        ) : (
          <Box textAlign="center" p={3}>
            <Typography variant="body2" color="text.secondary">
              No history data available for this device
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default DeviceDetail;